import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useI18n } from '@/i18n/LanguageContext';
import { Loader2 } from 'lucide-react';

const SharedHealthReportPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useI18n();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportHtml, setReportHtml] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!token) {
        setError('Invalid share link');
        setLoading(false);
        return;
      }

      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

        const response = await fetch(`${supabaseUrl}/functions/v1/get-health-report-snapshot`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${supabaseAnonKey}`,
            apikey: supabaseAnonKey,
          },
          body: JSON.stringify({ token }),
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(result.error || 'Failed to load shared report');
        }

        if (!result.html || typeof result.html !== 'string') {
          throw new Error('Invalid response from server');
        }

        // Render in-page so the share URL stays on app.onwheels.live and blob: navigation
        // quirks (private mode, CSP) cannot block the report.
        setReportHtml(result.html);
      } catch (err) {
        console.error('Error loading shared health report:', err);
        setError(err instanceof Error ? err.message : 'Failed to load health report');
        toast({
          title: t('healthReport.error') || 'Error',
          description: err instanceof Error ? err.message : 'Failed to load health report',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [token, toast, t]);

  if (reportHtml) {
    return (
      <iframe
        title={t('healthReport.title') || 'Health report'}
        srcDoc={reportHtml}
        className="min-h-screen w-full border-0 bg-[#f5f5f5]"
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f5f5f5' }}>
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-soft-blue" />
          <p className="text-gray-600">Loading health report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#f5f5f5' }}>
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm p-6 text-center">
          <h2 className="text-xl font-semibold mb-2 text-gray-900">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-lime text-black rounded-lg font-medium hover:bg-[#d4e848] transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default SharedHealthReportPage;
